/**
 * Status canônicos de assinatura (clube e assinante).
 * Espelha ResolveSubscriptionStatusService no backend.
 */
export const SUBSCRIPTION_STATUS = Object.freeze({
    WAITING: 'WAITING',
    ACTIVE: 'ACTIVE',
    EXPIRED: 'EXPIRED',
    CANCELLED: 'CANCELLED',
    INACTIVE: 'INACTIVE',
    SUSPENDED: 'SUSPENDED',
});

export const SUBSCRIPTION_STATUS_LABELS = Object.freeze({
    WAITING: 'Aguardando',
    ACTIVE: 'Ativa',
    EXPIRED: 'Expirada',
    CANCELLED: 'Cancelada',
    INACTIVE: 'Inativa',
    SUSPENDED: 'Suspensa',
});

const STATUS_META = {
    [SUBSCRIPTION_STATUS.ACTIVE]: {
        tone: 'success',
        color: 'success',
        badgeVariant: 'default',
        description: null,
    },
    [SUBSCRIPTION_STATUS.WAITING]: {
        tone: 'warning',
        color: 'warning',
        badgeVariant: 'secondary',
        description: 'Assinatura aguardando confirmação de pagamento.',
    },
    [SUBSCRIPTION_STATUS.EXPIRED]: {
        tone: 'danger',
        color: 'error',
        badgeVariant: 'destructive',
        description: 'Há cobranças em atraso.',
    },
    [SUBSCRIPTION_STATUS.CANCELLED]: {
        tone: 'danger',
        color: 'error',
        badgeVariant: 'destructive',
        description: 'Assinatura cancelada.',
    },
    [SUBSCRIPTION_STATUS.INACTIVE]: {
        tone: 'danger',
        color: 'error',
        badgeVariant: 'destructive',
        description: 'Assinatura inativa. O ciclo foi encerrado.',
    },
    [SUBSCRIPTION_STATUS.SUSPENDED]: {
        tone: 'warning',
        color: 'warning',
        badgeVariant: 'destructive',
        description: 'Suspensa por contestação no cartão (chargeback). Acesso bloqueado até a disputa terminar.',
    },
};

export function normalizeSubscriptionStatus(status) {
    const code = String(status || SUBSCRIPTION_STATUS.WAITING).toUpperCase();
    // Legado: CONCLUDED/PENDING vinham do fluxo antigo de pagamento
    if (code === 'CONCLUDED') return SUBSCRIPTION_STATUS.ACTIVE;
    if (code === 'PENDING') return SUBSCRIPTION_STATUS.WAITING;
    if (code === 'CANCELED') return SUBSCRIPTION_STATUS.CANCELLED;
    return code;
}

export function isSubscriptionActive(status) {
    return normalizeSubscriptionStatus(status) === SUBSCRIPTION_STATUS.ACTIVE;
}

export function isSubscriptionBlocked(status) {
    return !isSubscriptionActive(status);
}

export function subscriptionStatusLabel(status) {
    const code = normalizeSubscriptionStatus(status);
    return SUBSCRIPTION_STATUS_LABELS[code] || code;
}

export function subscriptionStatusBadgeVariant(status) {
    const code = normalizeSubscriptionStatus(status);
    return STATUS_META[code]?.badgeVariant || 'outline';
}

/**
 * Info completa para badges e avisos.
 */
export function getSubscriptionStatusInfo(subscription) {
    if (!subscription) {
        return {
            code: null,
            label: 'Sem assinatura',
            tone: 'neutral',
            color: 'default',
            badgeVariant: 'outline',
            description: null,
            accessAllowed: false,
            blocked: true,
        };
    }

    const code = normalizeSubscriptionStatus(subscription.status);
    const meta = STATUS_META[code] || {
        tone: 'neutral',
        color: 'default',
        badgeVariant: 'outline',
        description: null,
    };

    const accessAllowed =
        subscription.accessAllowed === true || code === SUBSCRIPTION_STATUS.ACTIVE;

    return {
        code,
        label: SUBSCRIPTION_STATUS_LABELS[code] || code,
        accessAllowed,
        blocked: subscription.blocked === true || !accessAllowed,
        ...meta,
    };
}

/** Opções para filtros/selects. */
export const SUBSCRIPTION_STATUS_OPTIONS = Object.values(SUBSCRIPTION_STATUS).map(value => ({
    value,
    label: SUBSCRIPTION_STATUS_LABELS[value],
}));
